import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type { AuthResponse } from '../types';
import { KeyRound, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';

export const MFAVerify: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [code, setCode] = useState('');

  const verifyMutation = useMutation({
    mutationFn: async (verificationCode: string) => {
      const tempToken = sessionStorage.getItem('mfa_temp_token');
      const mfaUserId = sessionStorage.getItem('mfa_user_id');
      const response = await api.post(
        '/auth/mfa/verify',
        {
          code: verificationCode,
          token: verificationCode,
          ...(mfaUserId ? { user_id: mfaUserId } : {}),
        },
        tempToken ? { headers: { Authorization: `Bearer ${tempToken}` } } : undefined
      );
      return (response.data?.data || response.data) as AuthResponse;
    },
    onSuccess: (data) => {
      sessionStorage.removeItem('mfa_temp_token');
      sessionStorage.removeItem('mfa_email');
      sessionStorage.removeItem('mfa_user_id');
      login(data.token, data.refresh_token, data.user);
      navigate('/');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verifyMutation.mutate(code);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Ambient background glows */}
      <div className="absolute top-1/4 -right-20 w-96 h-96 bg-primary-600/20 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-1/4 -left-20 w-96 h-96 bg-emerald-600/20 rounded-full blur-[128px] pointer-events-none" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-500 mx-auto flex items-center justify-center shadow-lg shadow-emerald-500/20 mb-6">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold text-white tracking-tight">
            Autenticação de Dois Fatores
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            Introduza o código de verificação da sua aplicação autenticadora
          </p>
        </div>

        <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 sm:p-10 shadow-2xl">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {verifyMutation.isError && (
              <div className="rounded-xl bg-rose-950/40 border border-rose-900/60 p-4">
                <p className="text-sm font-medium text-rose-300">
                  {getErrorMessage(verifyMutation.error)}
                </p>
              </div>
            )}

            <div>
              <label
                htmlFor="code"
                className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2 text-center"
              >
                Código de 6 dígitos
              </label>
              <input
                id="code"
                name="code"
                type="text"
                required
                className="w-full py-3.5 px-4 rounded-xl border border-slate-700 bg-slate-800/80 text-white placeholder-slate-600 text-center text-3xl tracking-[0.4em] font-mono font-bold focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                autoComplete="off"
                autoFocus
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={verifyMutation.isPending || code.length !== 6}
                className="btn-primary w-full py-3 text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-primary-500/20 disabled:opacity-50 transition-all"
              >
                {verifyMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> A validar código...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" /> Validar Acesso
                  </>
                )}
              </button>
            </div>

            <div className="text-center pt-2 border-t border-slate-800">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao Início de Sessão
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
